import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, message, Table, DatePicker, Statistic, Flex, Button, Tooltip, Space as AntSpace } from 'antd';
import { AreaChartOutlined, ShoppingCartOutlined, WalletOutlined, FileTextOutlined, ClockCircleOutlined, SyncOutlined, CheckCircleOutlined, SettingOutlined, RedoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import EmptyState from '../components/EmptyState';
import useDashboardStore from '../store/useDashboardStore';
import DashboardWidget from '../components/DashboardWidget';
import BarChartSimple from '../components/BarChartSimple';
import DonutChartSimple from '../components/DonutChartSimple';
import TrendChartSimple from '../components/TrendChartSimple';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// --- Local Utilities ---

const CountUp = ({ value, formatter = (v) => v, duration = 500 }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        let start = 0;
        const end = parseFloat(value);
        if (start === end) return;

        let startTime = null;
        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setDisplayValue(progress * (end - start) + start);
            if (progress < 1) {
                window.requestAnimationFrame(step);
            }
        };
        window.requestAnimationFrame(step);
    }, [value, duration]);

    return <span>{formatter(displayValue)}</span>;
};


const SystemStatus = ({ lastUpdate }) => (
    <Flex gap="16px" style={{ marginTop: '24px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.05)' }}>
        <div className="status-indicator">
            <span className="status-dot online"></span>
            System: Online
        </div>
        <div className="status-indicator">
            <span className="status-dot online"></span>
            ERP Sync: Connected
        </div>
        <div className="status-indicator">
            <SyncOutlined spin style={{ fontSize: '10px', color: '#8c8c8c' }} />
            <span style={{ fontSize: '11px', color: '#8c8c8c' }}>
                Last updated: {lastUpdate ? dayjs(lastUpdate).format('HH:mm:ss') : 'Just now'}
            </span>
        </div>
    </Flex>
);

const Dashboard = () => {
    const { isCustomizeMode, toggleCustomizeMode, widgets, resetLayout } = useDashboardStore();
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState([dayjs(), dayjs()]); // Default Today
    const [summary, setSummary] = useState({
        sales_ttc: 0,
        sales_ht: 0,
        total_vat: 0,
        invoice_count: 0,
        cash_collected: 0
    });
    const [topProducts, setTopProducts] = useState([]);
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const from = dates[0].format('YYYY-MM-DD');
            const to = dates[1].format('YYYY-MM-DD');
            const headers = { Authorization: `Bearer ${token}` };

            // Parallel Request
            const [sumRes, topRes, recRes] = await Promise.all([
                axios.get(`/api/dashboard/summary?from=${from}&to=${to}`, { headers }),
                axios.get(`/api/dashboard/top-products?from=${from}&to=${to}&limit=5`, { headers }),
                axios.get(`/api/dashboard/recent-invoices?limit=10`, { headers })
            ]);

            setSummary(sumRes.data);
            setTopProducts(topRes.data);
            setRecentInvoices(recRes.data);
            setLastUpdate(new Date());

        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.error || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dates]);

    // Helpers
    // formatCurrency imported from utils

    // Columns
    const invoiceColumns = [
        { title: 'Ref', dataIndex: 'ref', key: 'ref' },
        { title: 'Date', dataIndex: 'date', key: 'date' },
        { title: 'Total', dataIndex: 'total', key: 'total', render: formatCurrency },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
            render: (s) => s === '2' ? <Text type="success">Paid</Text> : s === '1' ? <Text type="warning">Unpaid</Text> : <Text type="secondary">Draft</Text>
        }
    ];

    const productColumns = [
        { title: 'Product', dataIndex: 'label', key: 'label' },
        { title: 'Qty Sold', dataIndex: 'qty', key: 'qty' },
        { title: 'Total Sales', dataIndex: 'total', key: 'total', render: formatCurrency }
    ];

    return (
        <div style={{ padding: '24px' }} className="fade-in-up">
            <Flex justify="space-between" align="end" style={{ marginBottom: 32 }}>
                <div>
                    <div className="section-title">Overview</div>
                    <Title level={2} style={{ margin: 0, fontWeight: 700 }}>Dashboard</Title>
                </div>
                <AntSpace size="middle">
                    <RangePicker
                        value={dates}
                        onChange={setDates}
                        allowClear={false}
                        style={{ borderRadius: '8px' }}
                    />
                    <Button
                        type={isCustomizeMode ? 'primary' : 'default'}
                        icon={isCustomizeMode ? <CheckCircleOutlined /> : <SettingOutlined />}
                        onClick={toggleCustomizeMode}
                        size="large"
                        style={{ borderRadius: '8px' }}
                    >
                        {isCustomizeMode ? 'Finish' : 'Customize'}
                    </Button>
                    {isCustomizeMode && (
                        <Tooltip title="Reset to default layout">
                            <Button icon={<RedoOutlined />} onClick={resetLayout} size="large" />
                        </Tooltip>
                    )}
                </AntSpace>
            </Flex>

            <Row gutter={[20, 20]}>
                {widgets.map((w, index) => {
                    const colSpan = w.size === 2 ? 12 : 6;

                    const renderWidgetContent = () => {
                        switch (w.id) {
                            case 'kpi-sales':
                                return <Statistic
                                    title={<Text strong style={{ fontSize: '13px', color: '#595959' }}>Sales (TTC)</Text>}
                                    value={summary.sales_ttc}
                                    formatter={(val) => <CountUp value={val} formatter={formatCurrency} />}
                                    prefix={<ShoppingCartOutlined style={{ color: '#1890ff', fontSize: '20px' }} />}
                                />;
                            case 'kpi-net':
                                return <Statistic
                                    title={<Text strong style={{ fontSize: '13px', color: '#595959' }}>Net Sales (HT)</Text>}
                                    value={summary.sales_ht}
                                    formatter={(val) => <CountUp value={val} formatter={formatCurrency} />}
                                    prefix={<AreaChartOutlined style={{ color: '#52c41a', fontSize: '20px' }} />}
                                />;
                            case 'kpi-cash':
                                return <Statistic
                                    title={<Text strong style={{ fontSize: '13px', color: '#595959' }}>Cash Collected</Text>}
                                    value={summary.cash_collected}
                                    formatter={(val) => <CountUp value={val} formatter={formatCurrency} />}
                                    prefix={<WalletOutlined style={{ color: '#faad14', fontSize: '20px' }} />}
                                />;
                            case 'kpi-invoices':
                                return (
                                    <>
                                        <Statistic
                                            title={<Text strong style={{ fontSize: '13px', color: '#595959' }}>Invoices</Text>}
                                            value={summary.invoice_count}
                                            formatter={(val) => <CountUp value={val} />}
                                            prefix={<FileTextOutlined style={{ fontSize: '20px' }} />}
                                        />
                                        <div style={{ marginTop: 8 }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>VAT: {formatCurrency(summary.total_vat)}</Text>
                                        </div>
                                    </>
                                );
                            case 'chart-trend':
                                // Generate dummy trend from summary or use static
                                const trendData = [
                                    summary.sales_ttc * 0.7,
                                    summary.sales_ttc * 0.9,
                                    summary.sales_ttc * 0.6,
                                    summary.sales_ttc * 1.1,
                                    summary.sales_ttc * 0.8,
                                    summary.sales_ttc * 0.95,
                                    summary.sales_ttc
                                ];
                                return <TrendChartSimple data={trendData} />;
                            case 'chart-payment':
                                const payData = [
                                    { label: 'Cash', value: Math.round(summary.sales_ttc * 0.6), color: '#1890ff' },
                                    { label: 'CliQ', value: Math.round(summary.sales_ttc * 0.3), color: '#52c41a' },
                                    { label: 'Visa', value: Math.round(summary.sales_ttc * 0.1), color: '#faad14' }
                                ];
                                return <DonutChartSimple data={payData} />;
                            case 'chart-products':
                                return <BarChartSimple data={topProducts} loading={loading} />;
                            case 'table-recent':
                                return (
                                    <Table
                                        dataSource={recentInvoices}
                                        columns={invoiceColumns}
                                        rowKey="id"
                                        pagination={false}
                                        size="middle"
                                        style={{ marginTop: '-16px' }}
                                        locale={{
                                            emptyText: <EmptyState
                                                message="No invoices found"
                                                description="Results will appear here."
                                            />
                                        }}
                                    />
                                );
                            case 'system-status':
                                return <SystemStatus lastUpdate={lastUpdate} />;
                            default:
                                return null;
                        }
                    };

                    return (
                        <Col
                            key={w.id}
                            xs={24}
                            lg={colSpan * 2}
                            xl={colSpan}
                            className={`widget-stagger-${(index % 6) + 1}`}
                        >
                            <DashboardWidget id={w.id} title={w.title} loading={loading}>
                                {renderWidgetContent()}
                            </DashboardWidget>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

export default Dashboard;
